import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useFetchPropertyMainSettingsData from 'pages/property/mainSettings/useFetchPropertyMainSettingsData';
import {
  getAirbnbCancellationPolicyLabel,
  isKnownAirbnbCancellationPolicy,
  KNOWN_CANCELLATION_POLICY_ORDER,
} from 'utils/airbnb/cancellationPolicyUtils';
import { isNonEmptyString } from 'utils/typeGuards';

/**
 * Both values are read from the cached server response, never from form state: sourcing the
 * current value from the form would be self-referential, since it would vanish the instant
 * another value is picked, with no way back until the server itself confirms the change.
 */
const useAirbnbCancellationPolicyOptions = (
  propertyUid?: string,
): { value: string; label: string }[] => {
  const { t } = useTranslation();
  const { mainSettingsData } = useFetchPropertyMainSettingsData(propertyUid);

  return useMemo(() => {
    const eligibleCancellationPolicies =
      mainSettingsData?.airbnbSettings?.eligibleCancellationPolicies ?? [];
    const savedCancellationPolicy =
      mainSettingsData?.airbnbSettings?.cancellationPolicy;

    const values = Array.from(
      new Set(
        [...eligibleCancellationPolicies, savedCancellationPolicy].filter(
          isNonEmptyString,
        ),
      ),
    );

    const knownValues = values
      .filter((value) => isKnownAirbnbCancellationPolicy(value))
      .toSorted(
        (a, b) =>
          KNOWN_CANCELLATION_POLICY_ORDER.indexOf(a) -
          KNOWN_CANCELLATION_POLICY_ORDER.indexOf(b),
      );
    const unknownValues = values
      .filter((value) => !isKnownAirbnbCancellationPolicy(value))
      .toSorted((a, b) => a.localeCompare(b));

    return [...knownValues, ...unknownValues].map((value) => ({
      value,
      label: getAirbnbCancellationPolicyLabel(value, t),
    }));
  }, [mainSettingsData, t]);
};

export default useAirbnbCancellationPolicyOptions;
